import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import {
  FaCheck,
  FaArrowRight,
  FaPlus,
  FaMinus,
  FaTrash,
  FaUserPlus,
  FaEnvelope
} from 'react-icons/fa';
import { Steps } from '@chakra-ui/react';
import BiensperienceLogo from '../BiensperienceLogo/BiensperienceLogo';
import { useData } from '../../contexts/DataContext';
import { useUser } from '../../contexts/UserContext';
import { useToast } from '../../contexts/ToastContext';
import { createExperience, updateExperience, addPlanItem as addExperiencePlanItem } from '../../utilities/experiences-api';
import { createPlan, addCollaborator, addPlanItem as addPlanPlanItem } from '../../utilities/plans-api';
import { searchUsers } from '../../utilities/users-api';
import { isDuplicateName } from '../../utilities/deduplication';
import { createFilter } from '../../utilities/trie';
import { useFormPersistence } from '../../hooks/useFormPersistence';
import { formatRestorationMessage } from '../../utilities/time-utils';
import { lang } from '../../lang.constants';
import { logger } from '../../utilities/logger';
import { Button, Pill, Alert, FormTooltip, Form } from '../design-system';
import FormField from '../FormField/FormField';
import Autocomplete from '../Autocomplete/Autocomplete';
import LocationAutocompleteInput from '../LocationAutocompleteInput/LocationAutocompleteInput';
import TagInput from '../TagInput/TagInput';
import PhotoUpload from '../PhotoUpload/PhotoUpload';
import NewDestinationModal from '../NewDestinationModal/NewDestinationModal';
import styles from './ExperienceWizardModal.module.css';

const STEPS = {
  BASIC_INFO: 0,
  MORE_DETAILS: 1,
  PLAN_ITEMS: 2,
  COLLABORATORS: 3,
  SUCCESS: 4,
};

// Step labels are set dynamically using lang constants in renderStepIndicator

/**
 * ExperienceWizardModal - A comprehensive multi-step wizard for creating a fully featured experience
 *
 * Step 1: Basic Info (Title, Overview, Destination)
 * Step 2: More Details (Address, Experience Types, Photos)
 * Step 3: Plan Items (Add multiple items with +/- interface)
 * Step 4: Collaborators (Search users or invite by email)
 * Step 5: Success
 */
export default function ExperienceWizardModal({ show, onClose, initialValues = {} }) {
  const navigate = useNavigate();
  const { destinations: destData, experiences: expData, addExperience, updateExperience: updateExpInContext } = useData();
  const { user } = useUser();
  const { success, error: showError } = useToast();

  // Step management
  const [currentStep, setCurrentStep] = useState(STEPS.BASIC_INFO);

  // Experience form state (Steps 1 & 2)
  const [experienceData, setExperienceData] = useState({
    name: '',
    description: '',
    destination: '',
    location: '',
    experience_type: [],
    visibility: 'public',
  });
  const [tags, setTags] = useState([]);
  const [destinationSearchTerm, setDestinationSearchTerm] = useState('');
  const [destinationInput, setDestinationInput] = useState('');

  // Created experience reference
  const [createdExperience, setCreatedExperience] = useState(null);
  const [createdPlan, setCreatedPlan] = useState(null);

  // Track if we're in the process of creating to prevent double submission
  const isCreatingRef = useRef(false);

  // Plan items state (Step 3)
  const [planItems, setPlanItems] = useState([
    { id: Date.now(), content: '', planning_days: 1, cost_estimate: 0 }
  ]);

  // Collaborators state (Step 4)
  const [collaboratorSearchTerm, setCollaboratorSearchTerm] = useState('');
  const [collaboratorSearchResults, setCollaboratorSearchResults] = useState([]);
  const [selectedCollaborators, setSelectedCollaborators] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMode, setInviteMode] = useState('search'); // 'search' or 'email'

  // UI state
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDestinationModal, setShowDestinationModal] = useState(false);
  const [prefillDestinationName, setPrefillDestinationName] = useState('');

  // Local destinations state
  const [destinations, setDestinations] = useState([]);

  // Form persistence - combines experienceData and tags
  const formData = { ...experienceData, experience_type: tags };
  const setFormData = useCallback((data) => {
    const { experience_type, ...expFields } = data;
    setExperienceData(expFields);
    if (experience_type) {
      setTags(experience_type);
    }
  }, []);

  const persistence = useFormPersistence(
    'experience-wizard-form',
    formData,
    setFormData,
    {
      enabled: show && !createdExperience, // Only persist before experience is created
      userId: user?._id,
      ttl: 24 * 60 * 60 * 1000,
      debounceMs: 1000,
      excludeFields: ['photos', 'photos_full'],
      onRestore: (savedData, age) => {
        const message = formatRestorationMessage(age, 'create');
        success(message, {
          duration: 20000,
          actions: [{
            label: lang.current.button.clearForm,
            onClick: () => {
              setExperienceData({
                name: '',
                description: '',
                destination: '',
                location: '',                experience_type: [],
              });
              setTags([]);
              setDestinationSearchTerm('');
              setDestinationInput('');
              persistence.clear();
            },
            variant: 'link'
          }]
        });
      }
    }
  );

  // Sync destinations from context
  useEffect(() => {
    if (destData) setDestinations(destData);
  }, [destData]);

  // Reset state when modal opens/closes. Prefill from `initialValues` when provided.
  useEffect(() => {
    if (!show) {
      setCurrentStep(STEPS.BASIC_INFO);
      setExperienceData({
        name: '',
        description: '',
        destination: '',
        location: '',
        experience_type: [],
        visibility: 'public',
      });
      setTags([]);
      setDestinationSearchTerm('');
      setDestinationInput('');
      setError('');
      setCreatedExperience(null);
      setCreatedPlan(null);
      isCreatingRef.current = false;
      setPlanItems([{ id: Date.now(), content: '', planning_days: 1, cost_estimate: 0 }]);
      setSelectedCollaborators([]);
      setCollaboratorSearchTerm('');
      setInviteEmail('');
      setInviteMode('search');
      setPrefillDestinationName('');
    } else {
      // When opening, apply any provided initialValues to prefill fields
      if (initialValues) {
        const pre = {
          name: initialValues.name || '',
          description: initialValues.description || '',
          destination: initialValues.destinationId || initialValues.destination || '',
          location: initialValues.location?.address || initialValues.map_location || '',          experience_type: initialValues.experience_type || [],
          visibility: initialValues.visibility || 'public',
        };
        setExperienceData(pre);
        // Prefill tags (experience types) when initial values are provided
        setTags(pre.experience_type || []);

        // If a destination name was provided, set search term to help Autocomplete
        if (initialValues.destinationName) {
          setDestinationSearchTerm(initialValues.destinationName);
          setDestinationInput(initialValues.destinationName);
        } else if (initialValues.destinationId) {
          // Try to derive name from dest list
          const destObj = (destinations || []).find(d => String(d._id) === String(initialValues.destinationId));
          if (destObj) {
            setDestinationSearchTerm(`${destObj.name}, ${destObj.country}`);
            setDestinationInput(`${destObj.name}, ${destObj.country}`);
          }
        }
      }
    }
  }, [show, initialValues, destinations]);

  // Build trie index for destination search
  const destinationTrieFilter = useMemo(() => {
    if (!destinations || destinations.length === 0) return null;
    const destItems = destinations.map(dest => ({
      id: dest._id,
      name: dest.name,
      country: dest.country,
      flag: dest.flag,
      experienceCount: (expData || []).filter(exp =>
        (typeof exp.destination === 'object' ? exp.destination._id : exp.destination) === dest._id
      ).length
    }));
    return createFilter({
      fields: [
        { path: 'name', score: 100 },
        { path: 'country', score: 50 },
      ]
    }).buildIndex(destItems);
  }, [destinations, expData]);

  // Handlers
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setExperienceData(prev => ({ ...prev, [name]: value }));
    setError('');
  }, []);

  const handleTagsChange = useCallback((newTags) => {
    setTags(newTags);
    setExperienceData(prev => ({ ...prev, experience_type: newTags }));
  }, []);

  const handleCreateDestinationClick = useCallback(() => {
    setPrefillDestinationName(destinationInput || destinationSearchTerm);
    setShowDestinationModal(true);
  }, [destinationInput, destinationSearchTerm]);

  const handleDestinationCreated = useCallback((newDestination) => {
    setDestinations(prev => [...prev, newDestination]);
    setExperienceData(prev => ({ ...prev, destination: newDestination._id }));
    setDestinationSearchTerm(`${newDestination.name}, ${newDestination.country}`);
    setShowDestinationModal(false);
  }, []);

  // Get filtered destinations for autocomplete
  const getFilteredDestinations = useCallback(() => {
    let filteredDestItems;
    if (destinationSearchTerm && destinationSearchTerm.trim() && destinationTrieFilter) {
      filteredDestItems = destinationTrieFilter.filter(destinationSearchTerm, { rankResults: true });
    } else if (destinationSearchTerm && destinationSearchTerm.trim()) {
      const allDestItems = destinations.map(dest => ({
        id: dest._id,
        name: dest.name,
        country: dest.country,
        flag: dest.flag,
        experienceCount: (expData || []).filter(exp =>
          (typeof exp.destination === 'object' ? exp.destination._id : exp.destination) === dest._id
        ).length
      }));
      const searchLower = destinationSearchTerm.toLowerCase();
      filteredDestItems = allDestItems.filter(dest => {
        const searchableText = [dest.name, dest.country].filter(Boolean).join(' ').toLowerCase();
        return searchableText.includes(searchLower);
      });
    } else {
      filteredDestItems = destinations.map(dest => ({
        id: dest._id,
        name: dest.name,
        country: dest.country,
        flag: dest.flag,
        experienceCount: (expData || []).filter(exp =>
          (typeof exp.destination === 'object' ? exp.destination._id : exp.destination) === dest._id
        ).length
      }));
    }

    // Add "Create New" option if searching
    if (destinationSearchTerm && destinationSearchTerm.length >= 2 && filteredDestItems.length === 0) {
      return [{
        id: 'create-new',
        name: `+ Create "${destinationSearchTerm}"`,
        country: 'New Destination',
        flag: '',
        experienceCount: 0,
        isCreateOption: true
      }];
    }

    return filteredDestItems;
  }, [destinationSearchTerm, destinationTrieFilter, destinations, expData]);

  // Plan Items handlers
  const handleAddPlanItem = useCallback(() => {
    setPlanItems(prev => [...prev, {
      id: Date.now(),
      content: '',
      planning_days: 1,
      cost_estimate: 0
    }]);
  }, []);

  const handleRemovePlanItem = useCallback((itemId) => {
    setPlanItems(prev => prev.filter(item => item.id !== itemId));
  }, []);

  const handlePlanItemChange = useCallback((itemId, field, value) => {
    setPlanItems(prev => prev.map(item =>
      item.id === itemId ? { ...item, [field]: value } : item
    ));
  }, []);

  const incrementDays = useCallback((itemId) => {
    setPlanItems(prev => prev.map(item =>
      item.id === itemId ? { ...item, planning_days: (item.planning_days || 1) + 1 } : item
    ));
  }, []);

  const decrementDays = useCallback((itemId) => {
    setPlanItems(prev => prev.map(item =>
      item.id === itemId && item.planning_days > 1
        ? { ...item, planning_days: item.planning_days - 1 }
        : item
    ));
  }, []);

  // Collaborator handlers
  const handleSearchCollaborators = useCallback(async (query) => {
    setCollaboratorSearchTerm(query);
    if (!query || query.length < 2) {
      setCollaboratorSearchResults([]);
      return;
    }
    try {
      const results = await searchUsers(query);
      // Filter out current user and already selected
      const filtered = (results || []).filter(u =>
        u._id !== user?._id &&
        !selectedCollaborators.some(c => c._id === u._id)
      );
      setCollaboratorSearchResults(filtered);
    } catch (err) {
      setCollaboratorSearchResults([]);
    }
  }, [user, selectedCollaborators]);

  const handleSelectCollaborator = useCallback((collaborator) => {
    setSelectedCollaborators(prev => [...prev, collaborator]);
    setCollaboratorSearchTerm('');
    setCollaboratorSearchResults([]);
  }, []);

  const handleRemoveCollaborator = useCallback((userId) => {
    setSelectedCollaborators(prev => prev.filter(c => c._id !== userId));
  }, []);

  // Step navigation
  const canProceedStep1 = experienceData.name?.trim() && experienceData.destination;
  const canProceedStep2 = true; // Optional fields
  const canProceedStep3 = true; // Optional - can skip adding items

  // Create experience after Step 1 (optimistic creation with basic info)
  const handleCreateExperience = async () => {
    if (isCreatingRef.current || createdExperience) return;

    setError('');
    setLoading(true);
    isCreatingRef.current = true;

    // Validation
    if (!experienceData.name?.trim()) {
      setError(lang.current.validation.experienceNameRequired);
      setLoading(false);
      isCreatingRef.current = false;
      return;
    }

    if (!experienceData.destination) {
      setError(lang.current.validation.selectDestinationRequired);
      setLoading(false);
      isCreatingRef.current = false;
      return;
    }

    // Check for duplicates
    if (isDuplicateName(expData || [], experienceData.name)) {
      setError(`An experience named "${experienceData.name}" already exists.`);
      setLoading(false);
      isCreatingRef.current = false;
      return;
    }

    try {
      // Create experience with basic info only (name, description, destination)
      const data = {
        name: experienceData.name.trim(),
        description: experienceData.description?.trim() || '',
        destination: experienceData.destination,
      };

      const experience = await createExperience(data);
      addExperience(experience);
      setCreatedExperience(experience);

      // Create a plan for this experience
      const plan = await createPlan(experience._id);
      setCreatedPlan(plan);

      // Clear form persistence since experience is now created
      persistence.clear();

      success(lang.current.success.experienceCreatedExcited);
      setCurrentStep(STEPS.MORE_DETAILS);
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to create experience';
      setError(errorMsg);
      isCreatingRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  // Update experience field on blur (for Step 2 optional fields)
  const handleFieldBlur = useCallback(async (fieldName, value) => {
    if (!createdExperience) return;

    // Don't update if value hasn't changed
    if (createdExperience[fieldName] === value) return;

    try {
      const updated = await updateExperience(createdExperience._id, { [fieldName]: value });
      setCreatedExperience(updated);
      updateExpInContext(updated);
    } catch (err) {
      logger.warn('Failed to update experience field', { fieldName }, err);
    }
  }, [createdExperience, updateExpInContext]);

  // Handle address blur
  const handleAddressBlur = useCallback(() => {
    handleFieldBlur('location', experienceData.location?.trim() || '');
  }, [experienceData.location, handleFieldBlur]);

  // Handle tags change with auto-save
  const handleTagsChangeWithSave = useCallback(async (newTags) => {
    setTags(newTags);
    setExperienceData(prev => ({ ...prev, experience_type: newTags }));

    // Auto-save to server if experience exists
    if (createdExperience) {
      try {
        const updated = await updateExperience(createdExperience._id, { experience_type: newTags });
        setCreatedExperience(updated);
        updateExpInContext(updated);
      } catch (err) {
        logger.warn('Failed to update experience types', {}, err);
      }
    }
  }, [createdExperience, updateExpInContext]);

  // Handle photo changes with auto-save
  const handlePhotoChange = useCallback(async (newData) => {
    setExperienceData(prev => ({ ...prev, ...newData }));

    if (!createdExperience) return;

    // Update photos on the server
    try {
      const updatePayload = {};
      if (newData.photos) updatePayload.photos = newData.photos;

      if (Object.keys(updatePayload).length > 0) {
        const updated = await updateExperience(createdExperience._id, updatePayload);
        setCreatedExperience(updated);
        updateExpInContext(updated);
      }
    } catch (err) {
      logger.warn('Failed to update photos', {}, err);
    }
  }, [createdExperience, updateExpInContext]);

  // Proceed from Step 2 to Step 3
  const handleProceedToStep3 = useCallback(async () => {
    // Save any pending updates before proceeding
    if (createdExperience) {
      try {
        const updateData = {};
        if (experienceData.location && (typeof experienceData.location === 'object' || experienceData.location.trim())) {
          updateData.location = experienceData.location;
        }
        if (tags.length > 0) {
          updateData.experience_type = tags;
        }
        if (experienceData.visibility) {
          updateData.visibility = experienceData.visibility;
        }

        if (Object.keys(updateData).length > 0) {
          const updated = await updateExperience(createdExperience._id, updateData);
          setCreatedExperience(updated);
          updateExpInContext(updated);
        }
      } catch (err) {
        logger.warn('Failed to save Step 2 updates', {}, err);
      }
    }
    setCurrentStep(STEPS.PLAN_ITEMS);
  }, [createdExperience, experienceData.location, tags, updateExpInContext]);

  // Step 3: Add Plan Items
  const handleSavePlanItems = async () => {
    setLoading(true);
    setError('');

    try {
      // Filter out empty items
      const validItems = planItems.filter(item => item.content?.trim());

      // Add each plan item to the experience
      const addedExperienceItems = [];
      for (const item of validItems) {
        const result = await addExperiencePlanItem(createdExperience._id, {
          text: item.content,
          planning_days: item.planning_days || 1,
          cost_estimate: parseFloat(item.cost_estimate) || 0,
        });
        // The last added item will be at the end of the plan_items array
        const newItem = result.plan_items[result.plan_items.length - 1];
        addedExperienceItems.push(newItem);
      }

      // Now add corresponding plan items to the user's plan
      for (const experienceItem of addedExperienceItems) {
        await addPlanPlanItem(createdPlan._id, {
          plan_item_id: experienceItem._id,
          text: experienceItem.text,
          planning_days: experienceItem.planning_days,
          cost: experienceItem.cost_estimate,
          url: experienceItem.url || null, // Match experience item's url field
        });
      }

      if (validItems.length > 0) {
        success(`Added ${validItems.length} plan item(s) to your plan!`);
      }

      setCurrentStep(STEPS.COLLABORATORS);
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to add plan items';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Step 4: Add Collaborators
  const handleSaveCollaborators = async () => {
    setLoading(true);
    setError('');

    try {
      // Add selected collaborators to the plan
      for (const collaborator of selectedCollaborators) {
        await addCollaborator(createdPlan._id, collaborator._id);
      }

      if (selectedCollaborators.length > 0) {
        success(`Added ${selectedCollaborators.length} collaborator(s)!`);
      }

      setCurrentStep(STEPS.SUCCESS);
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to add collaborators';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Handle going back
  const handleBack = useCallback(() => {
    if (currentStep > STEPS.BASIC_INFO && currentStep !== STEPS.SUCCESS) {
      // Can't go back to earlier steps after experience is created
      if (currentStep === STEPS.PLAN_ITEMS) {
        return;
      }
      setCurrentStep(prev => prev - 1);
      setError('');
    }
  }, [currentStep]);

  // Handle skipping optional steps
  const handleSkipStep = useCallback(() => {
    if (currentStep === STEPS.MORE_DETAILS) {
      setCurrentStep(STEPS.PLAN_ITEMS);
    } else if (currentStep === STEPS.PLAN_ITEMS) {
      setCurrentStep(STEPS.COLLABORATORS);
    } else if (currentStep === STEPS.COLLABORATORS) {
      setCurrentStep(STEPS.SUCCESS);
    }
  }, [currentStep]);

  // Handle final action - go to experience
  const handleGoToExperience = useCallback(() => {
    onClose();
    if (createdExperience) {
      navigate(`/experiences/${createdExperience._id}`);
    }
  }, [onClose, navigate, createdExperience]);

  // Close handler
  const handleClose = useCallback(() => {
    if (currentStep === STEPS.SUCCESS || !createdExperience) {
      onClose();
    } else {
      // Confirm closing if experience was created but wizard not complete
      if (window.confirm('Are you sure you want to close? Your experience has been created but you can add more details later.')) {
        onClose();
      }
    }
  }, [currentStep, createdExperience, onClose]);

  if (!show) return null;

  const stepLabels = {
    [STEPS.BASIC_INFO]: lang.current.experienceWizardModal.stepBasicInfo,
    [STEPS.MORE_DETAILS]: lang.current.experienceWizardModal.stepDetails,
    [STEPS.PLAN_ITEMS]: lang.current.experienceWizardModal.stepPlanItems,
    [STEPS.COLLABORATORS]: lang.current.experienceWizardModal.stepCollaborators,
    [STEPS.SUCCESS]: lang.current.experienceWizardModal.stepDone,
  };

  const stepItems = [
    { index: STEPS.BASIC_INFO, title: stepLabels[STEPS.BASIC_INFO] },
    { index: STEPS.MORE_DETAILS, title: stepLabels[STEPS.MORE_DETAILS] },
    { index: STEPS.PLAN_ITEMS, title: stepLabels[STEPS.PLAN_ITEMS] },
    { index: STEPS.COLLABORATORS, title: stepLabels[STEPS.COLLABORATORS] },
  ];

  const renderStepIndicator = () => (
    <>
      {/* Mobile/Tablet: compact dropdown */}
      <div className={styles.stepDropdown}>
        <select
          className={styles.stepDropdownSelect}
          value={currentStep}
          disabled
          aria-label={`Step ${currentStep + 1} of ${stepItems.length}: ${stepLabels[currentStep] ?? ''}`}
        >
          {stepItems.map((s) => (
            <option key={s.index} value={s.index}>
              {`Step ${s.index + 1} of ${stepItems.length}: ${s.title}`}
            </option>
          ))}
        </select>
      </div>

      {/* Desktop: horizontal stepper */}
      <div className={styles.stepStepper}>
        <Steps.Root step={currentStep} count={stepItems.length} size="sm" colorPalette="blue" px="5" pt="4" pb="2">
          <Steps.List>
            {stepItems.map((s) => (
              <Steps.Item key={s.index} index={s.index}>
                <Steps.Indicator>
                  <Steps.Status
                    complete={<FaCheck size={10} />}
                    incomplete={<Steps.Number />}
                    current={<Steps.Number />}
                  />
                </Steps.Indicator>
                <Steps.Title>{s.title}</Steps.Title>
                <Steps.Separator />
              </Steps.Item>
            ))}
          </Steps.List>
        </Steps.Root>
      </div>
    </>
  );

  const renderStep1 = () => (
    <Form onSubmit={(e) => { e.preventDefault(); handleCreateExperience(); }}>
      <FormField
        name="name"
        label={lang.current.experienceWizardModal.experienceTitle}
        type="text"
        value={experienceData.name || ''}
        onChange={handleChange}
        placeholder={lang.current.experienceWizardModal.experienceTitlePlaceholder}
        required
        tooltip={lang.current.helper.nameRequired}
      />

      <FormField
        name="description"
        label={lang.current.experienceWizardModal.overview}
        type="textarea"
        value={experienceData.description || ''}
        onChange={handleChange}
        placeholder={lang.current.experienceWizardModal.overviewPlaceholder}
        rows={3}
        tooltip={lang.current.helper.overviewOptional}
      />

      <div className={styles.mb4}>
        <Form.Group>
          <Form.Label>
            {lang.current.experienceWizardModal.destination}
            {' '}<span className={styles.textDanger}>*</span>{' '}
            <FormTooltip
              text={lang.current.helper.destinationRequired}
              placement="top"
            />
          </Form.Label>
          <Autocomplete
            placeholder={lang.current.experienceWizardModal.destinationPlaceholder}
            entityType="destination"
            items={getFilteredDestinations()}
            displayValue={destinationSearchTerm}
            onSelect={(destination) => {
              // Handle clear/unselect which passes `null` from Autocomplete
              if (!destination) {
                setExperienceData(prev => ({ ...prev, destination: '' }));
                setDestinationSearchTerm('');
                return;
              }

              if (destination.isCreateOption) {
                handleCreateDestinationClick();
                return;
              }

              setExperienceData(prev => ({
                ...prev,
                destination: destination._id || destination.id
              }));
              setDestinationSearchTerm(`${destination.name}, ${destination.country}`);
            }}
            onSearch={(query) => {
              setDestinationSearchTerm(query);
              setDestinationInput(query);
            }}
            size="md"
            emptyMessage={lang.current.placeholder.destination}
            disableFilter={true}
          />
          <small className={`form-text ${styles.formHelperText}`}>
            {lang.current.helper.destinationRequired}{' '}
            <a
              href="#"
              role="button"
              onClick={(e) => { e.preventDefault(); handleCreateDestinationClick(); }}
              className="btn btn-link p-0 align-baseline"
              style={{ textDecoration: 'none' }}
              aria-label={lang.current.helper.createNewDestination}
            >
              {lang.current.helper.createNewDestination}
            </a>
          </small>
        </Form.Group>
      </div>
    </Form>
  );

  const renderStep2 = () => (
    <Form onSubmit={(e) => { e.preventDefault(); handleProceedToStep3(); }}>
      <LocationAutocompleteInput
        name="location"
        label={lang.current.label.address}
        value={typeof experienceData.location === 'object' ? experienceData.location.address || '' : experienceData.location || ''}
        onSelect={(loc) => setExperienceData(prev => ({ ...prev, location: loc || '' }))}
        onBlur={handleAddressBlur}
        placeholder={lang.current.placeholder.address}
      />

      <div className={styles.mb4}>
        <Form.Label htmlFor="experience_type">
          {lang.current.experienceWizardModal.experienceTypes}
          <FormTooltip content={lang.current.helper.experienceTypesOptional} placement="top" />
        </Form.Label>
        <TagInput
          tags={tags}
          onChange={handleTagsChangeWithSave}
          placeholder={lang.current.experienceWizardModal.experienceTypesPlaceholder}
          maxTags={4}
        />
      </div>

      <div className={styles.mb4}>
        <Form.Label>
          {lang.current.experienceWizardModal.photos}
          <FormTooltip content={lang.current.helper.photosOptional} placement="top" />
        </Form.Label>
        <PhotoUpload data={experienceData} setData={handlePhotoChange} />
      </div>

      <div className={styles.mb4}>
        <Form.Group>
          <Form.Label htmlFor="wizard-visibility">
            {lang.current.label.experienceVisibility}
            {' '}
            <FormTooltip content={lang.current.tooltip.experienceVisibility} placement="top" />
          </Form.Label>
          <Form.Control
            as="select"
            id="wizard-visibility"
            name="visibility"
            value={experienceData.visibility || 'public'}
            onChange={handleChange}
          >
            <option value="public">{lang.current.label.experienceVisibilityPublic}</option>
            <option value="contributors">{lang.current.label.experienceVisibilityContributors}</option>
            <option value="private">{lang.current.label.experienceVisibilityPrivate}</option>
          </Form.Control>
        </Form.Group>
      </div>
    </Form>
  );

  const renderStep3 = () => (
    <div className={styles.planItemsContainer}>
      <p className={styles.stepDescription}>
        {lang.current.experienceWizardModal.noPlanItemsDescription}
      </p>

      <div className={styles.planItemsList}>
        {planItems.map((item, index) => (
          <div key={item.id} className={styles.planItemRow}>
            <div className={styles.planItemNumber}>{index + 1}</div>

            <div className={styles.planItemContent}>
              <input
                type="text"
                className={styles.planItemInput}
                placeholder={lang.current.experienceWizardModal.planItemPlaceholder}
                value={item.content}
                onChange={(e) => handlePlanItemChange(item.id, 'content', e.target.value)}
              />

              <div className={styles.planItemMeta}>
                <div className={styles.metaField}>
                  <label>
                    {lang.current.experienceWizardModal.planningDays}
                    <FormTooltip content={lang.current.helper.planningDays} placement="top" />
                  </label>
                  <div className={styles.counterControl}>
                    <button
                      type="button"
                      onClick={() => decrementDays(item.id)}
                      disabled={item.planning_days <= 1}
                      className={styles.counterBtn}
                    >
                      <FaMinus size={10} />
                    </button>
                    <span className={styles.counterValue}>{item.planning_days || 1}</span>
                    <button
                      type="button"
                      onClick={() => incrementDays(item.id)}
                      className={styles.counterBtn}
                    >
                      <FaPlus size={10} />
                    </button>
                  </div>
                </div>

                <div className={styles.metaField}>
                  <label>
                    {lang.current.experienceWizardModal.costEstimate}
                    <FormTooltip content={lang.current.helper.costEstimate} placement="top" />
                  </label>
                  <div className={styles.costInput}>
                    <span className={styles.currencySymbol}>$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0"
                      value={item.cost_estimate || ''}
                      onChange={(e) => handlePlanItemChange(item.id, 'cost_estimate', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            {planItems.length > 1 && (
              <button
                type="button"
                className={styles.removeItemBtn}
                onClick={() => handleRemovePlanItem(item.id)}
                title="Remove item"
              >
                <FaTrash size={14} />
              </button>
            )}
          </div>
        ))}
      </div>

      <button
        type="button"
        className={styles.addItemBtn}
        onClick={handleAddPlanItem}
      >
        <FaPlus size={14} />
        {lang.current.experienceWizardModal.addPlanItem}
      </button>
    </div>
  );

  const renderStep4 = () => (
    <div className={styles.collaboratorsContainer}>
      <p className={styles.stepDescription}>
        {lang.current.experienceWizardModal.noCollaboratorsDescription}
      </p>

      <div className={styles.inviteModeToggle}>
        <button
          type="button"
          className={`${styles.modeBtn} ${inviteMode === 'search' ? styles.active : ''}`}
          onClick={() => setInviteMode('search')}
        >
          <FaUserPlus size={14} />
          {lang.current.experienceWizardModal.searchUsers}
        </button>
        <button
          type="button"
          className={`${styles.modeBtn} ${inviteMode === 'email' ? styles.active : ''}`}
          onClick={() => setInviteMode('email')}
        >
          <FaEnvelope size={14} />
          {lang.current.experienceWizardModal.inviteByEmail}
        </button>
      </div>

      {inviteMode === 'search' ? (
        <div className={styles.searchSection}>
          <Autocomplete
            placeholder={lang.current.experienceWizardModal.searchUsersPlaceholder}
            entityType="user"
            items={collaboratorSearchResults}
            onSelect={handleSelectCollaborator}
            onSearch={handleSearchCollaborators}
            value={collaboratorSearchTerm}
            onChange={(e) => setCollaboratorSearchTerm(e.target.value)}
            showAvatar={true}
            showStatus={true}
            size="md"
            emptyMessage={lang.current.experienceWizardModal.searchUsersPlaceholder}
          />
        </div>
      ) : (
        <div className={styles.emailSection}>
          <FormField
            name="inviteEmail"
            label={lang.current.label.email}
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder={lang.current.experienceWizardModal.inviteEmailPlaceholder}
            helpText={lang.current.helper.inviteEmailHelpText}
          />
          <Button
            variant="outline"
            size="sm"
            disabled={!inviteEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail)}
            onClick={() => {
              success(`Invitation sent to ${inviteEmail}`);
              setInviteEmail('');
            }}
          >
            {lang.current.button.sendInvite}
          </Button>
        </div>
      )}

      {selectedCollaborators.length > 0 && (
        <div className={styles.selectedCollaborators}>
          <label>{lang.current.label.selectedCollaborators} ({selectedCollaborators.length})</label>
          <div className={styles.collaboratorPills}>
            {selectedCollaborators.map(collab => (
              <Pill
                key={collab._id}
                onDismiss={() => handleRemoveCollaborator(collab._id)}
              >
                {collab.name || collab.email}
              </Pill>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderSuccessStep = () => (
    <div className={styles.successContainer}>
      <div className={styles.successIcon}>
        <BiensperienceLogo type="clean" size="xl" />
      </div>
      <h3 className={styles.successTitle}>{lang.current.experienceWizardModal.doneTitle}</h3>
      <p className={styles.successMessage}>
        {lang.current.experienceWizardModal.doneMessage}
        {planItems.filter(i => i.content?.trim()).length > 0 && (
          <> You've added {planItems.filter(i => i.content?.trim()).length} plan items.</>
        )}
        {selectedCollaborators.length > 0 && (
          <> {selectedCollaborators.length} collaborator(s) have been invited.</>
        )}
      </p>
      <div className={styles.successActions}>
        <Button variant="gradient" size="lg" onClick={handleGoToExperience}>
          {lang.current.experienceWizardModal.goToExperience}
        </Button>
        <Button variant="outline" size="lg" onClick={onClose}>
          {lang.current.experienceWizardModal.close}
        </Button>
      </div>
    </div>
  );

  const renderFooter = () => {
    if (currentStep === STEPS.SUCCESS) return null;

    return (
      <div className={styles.modalFooter}>
        <div className={styles.footerLeft}>
          {currentStep === STEPS.BASIC_INFO && (
            <button type="button" className={styles.backButton} onClick={handleClose}>
              {lang.current.button.cancel}
            </button>
          )}
          {(currentStep === STEPS.MORE_DETAILS || currentStep === STEPS.PLAN_ITEMS || currentStep === STEPS.COLLABORATORS) && (
            <button type="button" className={styles.skipLink} onClick={handleSkipStep}>
              {lang.current.experienceWizardModal.skip}
            </button>
          )}
        </div>

        <div className={styles.footerRight}>
          {currentStep === STEPS.BASIC_INFO && (
            <Button
              variant="gradient"
              size="lg"
              onClick={handleCreateExperience}
              disabled={loading || !canProceedStep1}
            >
              {loading ? lang.current.button.creating : lang.current.experienceWizardModal.create}
            </Button>
          )}
          {currentStep === STEPS.MORE_DETAILS && (
            <Button
              variant="gradient"
              size="lg"
              onClick={handleProceedToStep3}
              disabled={loading}
            >
              {lang.current.experienceWizardModal.next}
              <FaArrowRight size={12} className={styles.ms2} />
            </Button>
          )}
          {currentStep === STEPS.PLAN_ITEMS && (
            <Button
              variant="gradient"
              size="lg"
              onClick={handleSavePlanItems}
              disabled={loading}
            >
              {loading ? lang.current.button.saving : planItems.filter(i => i.content?.trim()).length > 0 ? lang.current.button.saveContinue : lang.current.button.continue}
            </Button>
          )}
          {currentStep === STEPS.COLLABORATORS && (
            <Button
              variant="gradient"
              size="lg"
              onClick={handleSaveCollaborators}
              disabled={loading}
            >
              {loading ? lang.current.button.adding : selectedCollaborators.length > 0 ? lang.current.button.addFinish : lang.current.experienceWizardModal.finish}
            </Button>
          )}
        </div>
      </div>
    );
  };

  const modalContent = (
    <div className={styles.modalContainer} tabIndex="-1" role="dialog" aria-modal="true">
      <div className={styles.modalDialog}>
        <div className={styles.modalContent}>
          {/* Header */}
          <div className={styles.modalHeader}>
            <h5 className={styles.modalTitle}>
              {currentStep === STEPS.SUCCESS ? lang.current.experienceWizardModal.doneTitle : lang.current.experienceWizardModal.basicInfoTitle}
            </h5>
            <button
              type="button"
              className={styles.closeButton}
              onClick={handleClose}
              aria-label="Close"
            >
              &times;
            </button>
          </div>

          {/* Step Indicator */}
          {currentStep !== STEPS.SUCCESS && renderStepIndicator()}

          {/* Body */}
          <div className={styles.modalBody}>
            {error && (
              <Alert type="danger" className={styles.mb4} dismissible onDismiss={() => setError('')}>
                {error}
              </Alert>
            )}

            <div className={styles.stepContent}>
              {currentStep === STEPS.BASIC_INFO && renderStep1()}
              {currentStep === STEPS.MORE_DETAILS && renderStep2()}
              {currentStep === STEPS.PLAN_ITEMS && renderStep3()}
              {currentStep === STEPS.COLLABORATORS && renderStep4()}
              {currentStep === STEPS.SUCCESS && renderSuccessStep()}
            </div>
          </div>

          {/* Footer */}
          {renderFooter()}
        </div>
      </div>

      {/* Nested Destination Modal */}
      <NewDestinationModal
        show={showDestinationModal}
        onClose={() => setShowDestinationModal(false)}
        onDestinationCreated={handleDestinationCreated}
        prefillName={prefillDestinationName}
      />
    </div>
  );

  return createPortal(modalContent, document.body);
}

ExperienceWizardModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  // initialValues is a free-form object used to prefill fields. Example:
  // { destinationId: '...', destinationName: 'Paris, France', name: 'Weekend in Paris' }
  initialValues: PropTypes.object,
};
